import { usePreviewFilterStore } from "@/stores/previewFilterStore";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent
} from "@/components/ui/collapsible";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider
} from "@/components/ui/tooltip";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useState } from "react";
import { ChevronDown, ChevronUp, SlidersHorizontal, RefreshCw } from "lucide-react";

export function PreviewFilterControls() {
  const [open, setOpen] = useState(false);
  const {
    // Channel filters
    showDescription,
    showPrice,
    showPlans,
    showNumbers,
    showInAllCategories,
    showLocalChannelsCategory,
    hideSerialNumbers,
    sortOrder,
    setShowDescription,
    setShowPrice,
    setShowPlans,
    setShowNumbers,
    setShowInAllCategories,
    setShowLocalChannelsCategory,
    setHideSerialNumbers,
    setSortOrder,
    
    // Plan filters
    showPlanDescription,
    showPlanMonthlyPrice,
    showPlanYearlyPrice,
    setShowPlanDescription,
    setShowPlanMonthlyPrice,
    setShowPlanYearlyPrice,
    
    // Category filters
    showCategoryDescription,
    showCategoryPrice,
    setShowCategoryDescription,
    setShowCategoryPrice,
    
    resetFilters
  } = usePreviewFilterStore();

  const activeFilterCount = [
    // Channel filters
    showDescription,
    showPrice, 
    showPlans,
    showNumbers, 
    showInAllCategories,
    showLocalChannelsCategory,
    hideSerialNumbers,
    
    // Plan filters
    showPlanDescription,
    showPlanMonthlyPrice,
    showPlanYearlyPrice,
    
    // Category filters
    showCategoryDescription,
    showCategoryPrice
  ].filter(Boolean).length;

  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
      className="w-full border rounded-md p-2"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <SlidersHorizontal className="h-4 w-4 text-muted-foreground" />
          <h4 className="text-sm font-medium">
            Display Filters
            {activeFilterCount < 12 && (
              <span className="ml-2 bg-primary text-primary-foreground text-xs rounded-full px-2 py-0.5">
                {activeFilterCount}
              </span>
            )}
          </h4>
        </div>
        <div className="flex gap-1">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={resetFilters}
                >
                  <RefreshCw className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Reset filters</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          
          <CollapsibleTrigger asChild>
            <Button variant="ghost" size="sm">
              {open ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </Button>
          </CollapsibleTrigger>
        </div>
      </div>
      
      <CollapsibleContent className="mt-4 space-y-6">
        {/* Channel Filters Section */}
        <div className="space-y-3">
          <h5 className="text-sm font-medium">Channel Filters</h5>
          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center space-x-2">
              <Checkbox 
                id="show-description" 
                checked={showDescription}
                onCheckedChange={(checked) => setShowDescription(checked === true)}
              />
              <Label htmlFor="show-description">Show Description</Label>
            </div>
            
            <div className="flex items-center space-x-2">
              <Checkbox 
                id="show-price" 
                checked={showPrice}
                onCheckedChange={(checked) => setShowPrice(checked === true)}
              />
              <Label htmlFor="show-price">Show Price</Label>
            </div>
            
            <div className="flex items-center space-x-2">
              <Checkbox 
                id="show-plans" 
                checked={showPlans}
                onCheckedChange={(checked) => setShowPlans(checked === true)}
              />
              <Label htmlFor="show-plans">Show Plans</Label>
            </div>
            
            <div className="flex items-center space-x-2">
              <Checkbox 
                id="show-numbers" 
                checked={showNumbers}
                onCheckedChange={(checked) => setShowNumbers(checked === true)}
                disabled
              />
              <Label htmlFor="show-numbers" className="text-muted-foreground">
                Show Channel Numbers (Required)
              </Label>
            </div>
            
            <div className="flex items-center space-x-2">
              <Checkbox
                id="hide-serial-numbers"
                checked={hideSerialNumbers}
                onCheckedChange={(checked) => setHideSerialNumbers(checked === true)}
              />
              <Label htmlFor="hide-serial-numbers">Hide Serial Numbers</Label>
            </div>

            <div className="flex items-center space-x-2">
              <Label htmlFor="sort-order" className="min-w-[80px]">Sort Order:</Label>
              <Select value={sortOrder} onValueChange={(value) => setSortOrder(value as 'asc' | 'desc')}>
                <SelectTrigger id="sort-order" className="w-[150px]">
                  <SelectValue placeholder="Select sort order" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="asc">Ascending (1-9)</SelectItem>
                  <SelectItem value="desc">Descending (9-1)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="border-t pt-3 mt-2">
            <p className="text-xs text-muted-foreground mb-3">
              <strong>Hide Serial Numbers:</strong> For sequential providers only. Hides the serial number column in preview, WordPress export, and PDF download.
            </p>
            
            <div className="flex items-center space-x-2">
              <Checkbox 
                id="show-in-all-categories" 
                checked={showInAllCategories}
                onCheckedChange={(checked) => setShowInAllCategories(checked === true)}
              />
              <Label htmlFor="show-in-all-categories">Show Channels In All Categories</Label>
            </div>
            <p className="text-xs text-muted-foreground mt-1 ml-6">
              When enabled, channels will appear in all assigned categories. Otherwise, they will only show in their default category.
            </p>
            
            <div className="flex items-center space-x-2 mt-3">
              <Checkbox 
                id="show-local-channels-category" 
                checked={showLocalChannelsCategory}
                onCheckedChange={(checked) => setShowLocalChannelsCategory(checked === true)}
              />
              <Label htmlFor="show-local-channels-category">Show Local Channels as Category</Label>
            </div>
            <p className="text-xs text-muted-foreground mt-1 ml-6">
              When enabled, local channels will be grouped under a separate "Local Channels" category heading.
            </p>
          </div>
        </div>
        
        {/* Plan Filters Section */}
        <div className="space-y-3 border-t pt-4">
          <h5 className="text-sm font-medium">Plan Filters</h5>
          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center space-x-2">
              <Checkbox 
                id="show-plan-description" 
                checked={showPlanDescription}
                onCheckedChange={(checked) => setShowPlanDescription(checked === true)}
              />
              <Label htmlFor="show-plan-description">Show Description</Label>
            </div>
            
            <div className="flex items-center space-x-2">
              <Checkbox 
                id="show-plan-monthly-price" 
                checked={showPlanMonthlyPrice}
                onCheckedChange={(checked) => setShowPlanMonthlyPrice(checked === true)}
              />
              <Label htmlFor="show-plan-monthly-price">Show Monthly Price</Label>
            </div>
            
            <div className="flex items-center space-x-2">
              <Checkbox 
                id="show-plan-yearly-price" 
                checked={showPlanYearlyPrice}
                onCheckedChange={(checked) => setShowPlanYearlyPrice(checked === true)}
              />
              <Label htmlFor="show-plan-yearly-price">Show Yearly Price</Label>
            </div>
          </div>
        </div>
        
        {/* Category Filters Section */}
        <div className="space-y-3 border-t pt-4">
          <h5 className="text-sm font-medium">Category Filters</h5>
          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center space-x-2">
              <Checkbox 
                id="show-category-description" 
                checked={showCategoryDescription}
                onCheckedChange={(checked) => setShowCategoryDescription(checked === true)}
              />
              <Label htmlFor="show-category-description">Show Description</Label>
            </div>
            
            <div className="flex items-center space-x-2">
              <Checkbox 
                id="show-category-price" 
                checked={showCategoryPrice}
                onCheckedChange={(checked) => setShowCategoryPrice(checked === true)}
              />
              <Label htmlFor="show-category-price">Show Price</Label>
            </div>
          </div>
        </div>
        
        <div className="pt-2 text-xs text-muted-foreground">
          These settings control what's shown in the preview, WordPress export, and PDF download.
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
} 